import { useRef, useState } from "react";
import { HiUpload } from "react-icons/hi";
import { translate } from "ui/i18n";

interface Props {
  label?: string;
  height?: string;
  disabled?: boolean;
  className?: string;
  onChange?: (value: FileList | "") => void;
  optional?: boolean;
  accept?: string;
}

export function INFileInput({
  label,
  height = "46px",
  disabled,
  className,
  onChange,
  optional,
  accept = "image/jpeg,image/png,application/pdf",
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const isFileAccepted = (file: File): boolean => {
    return accept
      .split(",")
      .map((token) => token.trim().toLowerCase())
      .some((token) => {
        if (token.startsWith("."))
          return file.name.toLowerCase().endsWith(token);
        if (token.endsWith("/*"))
          return file.type.startsWith(token.slice(0, -1));
        return file.type === token;
      });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const dt = new DataTransfer();
    Array.from(files)
      .filter(isFileAccepted)
      .forEach((file) => dt.items.add(file));

    if (dt.files.length > 0) onChange?.(dt.files);
  };

  return (
    <div className={`flex flex-col ${className}`}>
      {label && (
        <span className="text-sm font-bold text-darkGrey-700">
          {label}
          {optional && (
            <span className="ms-1 text-xs">
              ({translate("ns1:Input.Optional")})
            </span>
          )}
        </span>
      )}

      <div
        className={`mt-2 w-full cursor-pointer rounded-xl border border-dashed p-8 transition-colors ${
          isDragging
            ? "bg-secondaryGrey-300"
            : "border-secondaryGrey-500 hover:bg-secondaryGrey-300"
        } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex items-center justify-center">
          <HiUpload size={32} className="me-4 text-secondaryGrey-800" />
          <div className="flex flex-col">
            <span className="mb-1 text-sm font-bold text-secondaryGrey-800 md:text-base">
              {translate("ns1:Input.Labels.ImportFiles")}
            </span>
            <span className="text-xs font-medium text-darkGrey-500">
              {translate("ns1:Input.Labels.ImagesFiles")}
            </span>
          </div>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        disabled={disabled}
        id="file-input"
        onChange={(e) => onChange?.(e.target.files ? e.target.files : "")}
        style={{ height }}
        className="hidden"
      />
    </div>
  );
}
